import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import eq from 'webapp/helpers/eq';
import makeArrayHelper from 'webapp/helpers/make-array';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButton from 'webapp/components/submit-button';

interface StepItem {
	icon: IconName;
	text: string;
}

interface Step {
	id: number;
	label: string;
	title: string;
	description: string;
	items: StepItem[];
}

export default class OnboardingPopupGenie extends PopupInnerModalComponent<PopupData.None> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked selectedIndex = 0;

	steps: Step[] = [
		{
			id: 1,
			label: 'NEW',
			title: 'Meet Genie, your AI analyst',
			description:
				'Getting answers from your data used to take hours. Not anymore. Ask Genie a question, and get an answer in seconds.',
			items: [
				{
					icon: 'AiSummary',
					text: 'Analyze performance instantly',
				},
				{
					icon: 'Blocks',
					text: 'Create metrics & dashboards',
				},
				{
					icon: 'Share',
					text: 'Share insights with anyone',
				},
			],
		},
		{
			id: 2,
			label: 'ANALYZE',
			title: 'Get instant answers about performance',
			description:
				'Ask a question about your performance. Genie analyzes your metrics and explains what’s happening in plain language.',
			items: [
				{
					icon: 'Check',
					text: 'Turn questions into insights',
				},
				{
					icon: 'Check',
					text: 'See answers with charts',
				},
				{
					icon: 'Check',
					text: 'Ask follow-up questions',
				},
			],
		},
		{
			id: 3,
			label: 'CREATE',
			title: 'Build what you need, with a prompt',
			description:
				'Tell Genie what you want to track or visualize, and it builds the metric, dashboard, or visualization for you.',
			items: [
				{
					icon: 'Check',
					text: 'Create dashboards instantly',
				},
				{
					icon: 'Check',
					text: 'Build custom metrics',
				},
				{
					icon: 'Check',
					text: 'Visualize any KPI',
				},
			],
		},
		{
			id: 4,
			label: 'SHARE',
			title: 'Share the conversation, not a screenshot',
			description:
				"Share a link to your conversation with teammates, clients, or leadership, even if they're not in Databox.",
			items: [
				{
					icon: 'Check',
					text: 'Share the full conversation',
				},
				{
					icon: 'Check',
					text: 'Keep the charts and context',
				},
				{
					icon: 'Check',
					text: 'Send a shareable link',
				},
			],
		},
	];

	get themeId() {
		return this.navigationService.theme.id;
	}

	get currentStep() {
		return this.steps[this.selectedIndex];
	}

	get nextBtnText() {
		return this.selectedIndex === this.steps.length - 1 ? 'Chat with Genie' : 'Next';
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.userSettingsService.hasSeenOnboardingGeniePopup = true;
		this.analyticsService.event(TrackingEvent.GENIE_INTRO_MODAL_SHOWN);
	}

	@action
	selectStep(index: number) {
		this.selectedIndex = index;
	}

	@action
	back() {
		if (this.selectedIndex === 0) {
			return;
		}

		this.selectedIndex--;
	}

	@action
	next() {
		if (this.selectedIndex === this.steps.length - 1) {
			this.analyticsService.event(TrackingEvent.GENIE_INTRO_MODAL_CTA_CLICKED);
			this.navigationService.transitionToRoute('genie');
			this.close();

			return;
		}

		this.selectedIndex++;
	}

	<template>
		<div class="content">
			<div class="content__left">
				<span class="badge2 badge2--blue">{{this.currentStep.label}}</span>
				<h3>{{this.currentStep.title}}</h3>
				<p>{{this.currentStep.description}}</p>
				<ul>
					{{#each this.currentStep.items as |item|}}
						<li>
							<CommonUiIconComponent @name={{item.icon}} @size="md" />
							<span>{{item.text}}</span>
						</li>
					{{/each}}
				</ul>

				<div class="controls2">
					<div class="controls2__left">
						{{#unless (eq this.selectedIndex 0)}}
							<SubmitButton
								@text="Back"
								@theme="grey-outlined"
								@onTrigger={{this.back}}
							/>
						{{/unless}}

						<SubmitButton @text={{this.nextBtnText}} @onTrigger={{this.next}} />
					</div>
				</div>
			</div>
			<div class="content__right">
				<img
					src="/images/onboarding/genie/{{this.currentStep.id}}-{{this.themeId}}.jpg"
					alt=""
				/>
				<ul class="current-step-indicator">
					{{#each (makeArrayHelper length=this.steps.length) as |_item index|}}
						<li
							role="button"
							class={{if (eq this.selectedIndex index) "selected" ""}}
							{{on "click" (fn this.selectStep index)}}
						></li>
					{{/each}}
				</ul>
			</div>
		</div>
	</template>
}
